import React, { useCallback, useEffect, useRef } from 'react';
import type { TabsProps, TabsVariant } from './types';

// ─── Style maps ───────────────────────────────────────────────────────────────

const LIST_CLASSES: Record<TabsVariant, string> = {
  default:
    'flex border-b border-neutral-200 dark:border-neutral-700',
  pill:
    'flex gap-1 p-1',
  segmented:
    'flex gap-0 p-0.5 bg-neutral-100 dark:bg-neutral-800 rounded-lg',
};

// Active tab styles
const TAB_ACTIVE: Record<TabsVariant, string> = {
  default:
    'text-brand-600 dark:text-brand-400 border-b-[3px] border-brand-500 -mb-px',
  pill:
    'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm',
  segmented:
    'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm',
};

// Inactive tab styles
const TAB_INACTIVE: Record<TabsVariant, string> = {
  default:
    'text-neutral-500 dark:text-neutral-400 border-b-[3px] border-transparent -mb-px ' +
    'hover:text-neutral-700 dark:hover:text-neutral-200 hover:border-neutral-300 dark:hover:border-neutral-600',
  pill:
    'text-neutral-500 dark:text-neutral-400 ' +
    'hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700/50',
  segmented:
    'text-neutral-500 dark:text-neutral-400 ' +
    'hover:text-neutral-700 dark:hover:text-neutral-200',
};

// Tab base (shared across all variants)
const TAB_BASE =
  'relative inline-flex items-center justify-center gap-1.5 min-h-[44px] sm:min-h-0 ' +
  'text-sm font-medium whitespace-nowrap select-none outline-none transition-colors duration-150 ' +
  'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset';

const TAB_SIZE: Record<TabsVariant, string> = {
  default: 'px-4 py-2.5',
  pill:    'px-3 py-1.5 rounded-md',
  segmented: 'px-3 py-1.5 rounded-md',
};

const TAB_DISABLED = 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed pointer-events-none';

// ─── Tabs ─────────────────────────────────────────────────────────────────────

export function Tabs({
  items,
  activeId,
  onChange,
  variant = 'default',
  scrollable = false,
  fullWidth = false,
}: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  // Auto-enable scrollable when items > 5
  const isScrollable = scrollable || items.length > 5;

  // ── Arrow-key navigation within tablist ──────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const enabledIndices = items
        .map((item, i) => (item.disabled ? null : i))
        .filter((i): i is number => i !== null);

      const posInEnabled = enabledIndices.indexOf(currentIndex);

      let nextEnabled: number | undefined;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextEnabled = enabledIndices[(posInEnabled + 1) % enabledIndices.length];
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextEnabled =
          enabledIndices[
            (posInEnabled - 1 + enabledIndices.length) % enabledIndices.length
          ];
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextEnabled = enabledIndices[0];
      } else if (e.key === 'End') {
        e.preventDefault();
        nextEnabled = enabledIndices[enabledIndices.length - 1];
      }

      if (nextEnabled !== undefined) {
        const btn = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[
          nextEnabled
        ];
        btn?.focus();
        onChange(items[nextEnabled].id);
      }
    },
    [items, onChange],
  );

  // ── Alt+1…9 shortcut keys ──────────────────────────────────────────────
  useEffect(() => {
    function handleGlobalKey(e: KeyboardEvent) {
      if (!e.altKey) return;
      const digit = parseInt(e.key, 10);
      if (isNaN(digit) || digit < 1 || digit > 9) return;
      const target = items[digit - 1];
      if (target && !target.disabled) {
        e.preventDefault();
        onChange(target.id);
      }
    }
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [items, onChange]);

  // ── Scroll active tab into view when it changes ───────────────────────
  useEffect(() => {
    if (!isScrollable || !listRef.current) return;
    const activeBtn = listRef.current.querySelector<HTMLElement>('[aria-selected="true"]');
    activeBtn?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeId, isScrollable]);

  const listClasses = [
    LIST_CLASSES[variant],
    isScrollable ? 'overflow-x-auto scrollbar-none' : '',
    fullWidth && variant !== 'default' ? 'w-full' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-orientation="horizontal"
      className={listClasses}
    >
      {items.map((item, index) => {
        const isActive = item.id === activeId;
        const isDisabled = Boolean(item.disabled);

        const tabClasses = [
          TAB_BASE,
          TAB_SIZE[variant],
          isDisabled
            ? TAB_DISABLED
            : isActive
              ? TAB_ACTIVE[variant]
              : TAB_INACTIVE[variant],
          fullWidth ? 'flex-1' : '',
        ]
          .filter(Boolean)
          .join(' ');

        // Badge label for screen readers ("N unread" appended to tab label)
        const srBadgeLabel =
          item.badge !== undefined ? `, ${item.badge} unread` : '';

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${item.id}`}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            tabIndex={isActive ? 0 : -1}
            className={tabClasses}
            onClick={() => !isDisabled && onChange(item.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {/* Screen-reader full label (icon is hidden, badge count appended) */}
            <span className="sr-only">
              {item.label}
              {srBadgeLabel}
            </span>

            {/* Visual content — aria-hidden since sr-only span covers it */}
            <span className="inline-flex items-center gap-1.5" aria-hidden="true">
              {item.icon && (
                <span className="h-4 w-4 flex items-center shrink-0">{item.icon}</span>
              )}
              <span>{item.label}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span
                  className={[
                    'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1',
                    'rounded-full text-[10px] font-semibold leading-none',
                    isActive
                      ? variant === 'default'
                        ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                        : 'bg-brand-500/20 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300'
                      : 'bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
